import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { HDate } from "hebcal";

// Hebrew letters for numbers
const hebrewNumerals = {
    1: 'א', 2: 'ב', 3: 'ג', 4: 'ד', 5: 'ה', 6: 'ו', 7: 'ז', 8: 'ח', 9: 'ט',
    10: 'י', 11: 'י"א', 12: 'י"ב', 13: 'י"ג', 14: 'י"ד', 15: 'ט"ו', 16: 'ט"ז',
    17: 'י"ז', 18: 'י"ח', 19: 'י"ט', 20: 'כ', 21: 'כ"א', 22: 'כ"ב', 23: 'כ"ג',
    24: 'כ"ד', 25: 'כ"ה', 26: 'כ"ו', 27: 'כ"ז', 28: 'כ"ח', 29: 'כ"ט', 30: 'ל'
};

// Convert number to Hebrew letters for year
const numberToHebrewYear = (year) => {
    // Get last 3 digits of Hebrew year (e.g., 5786 -> 786)
    const shortYear = year % 1000;
    const hundreds = Math.floor(shortYear / 100);
    const tens = Math.floor((shortYear % 100) / 10);
    const ones = shortYear % 10;

    const hundredsLetters = ['', 'ק', 'ר', 'ש', 'ת', 'תק', 'תר', 'תש', 'תת', 'תתק'];
    const tensLetters = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
    const onesLetters = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];

    // Handle special cases for 15 and 16
    if (tens === 1 && ones === 5) return hundredsLetters[hundreds] + 'ט"ו';
    if (tens === 1 && ones === 6) return hundredsLetters[hundreds] + 'ט"ז';

    let result = hundredsLetters[hundreds] + tensLetters[tens] + onesLetters[ones];

    // Add gershayim before last letter
    if (result.length > 1) {
        result = result.slice(0, -1) + '"' + result.slice(-1);
    } else if (result.length === 1) {
        result = result + "'";
    }

    return result;
};

// Convert Gregorian date to Hebrew date string with Hebrew letters
const toHebrewDate = (date) => {
    if (!date) return "";
    try {
        const hDate = new HDate(date);
        const day = hDate.getDate();
        const monthName = hDate.getMonthName('h');
        const year = hDate.getFullYear();

        const hebrewDay = hebrewNumerals[day] || day;
        const hebrewYear = numberToHebrewYear(year);

        return `${hebrewDay} ${monthName} ${hebrewYear}`;
    } catch (error) {
        console.error("Error converting to Hebrew date:", error);
        return "";
    }
};

// Custom input component that displays Hebrew date
const HebrewDateInput = forwardRef(({ value, onClick, placeholder }, ref) => {
    let hebrewValue = "";
    if (value) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            hebrewValue = toHebrewDate(date);
        }
    }

    return (
        <input
            type="text"
            className="form-control hebrew-date-input"
            onClick={onClick}
            ref={ref}
            value={hebrewValue}
            placeholder={placeholder || "בחר תאריך"}
            readOnly
        />
    );
});

// Custom day content to show Hebrew date
const renderDayContents = (day, date) => {
    try {
        const hDate = new HDate(date);
        const hebrewDay = hebrewNumerals[hDate.getDate()] || hDate.getDate();
        return (
            <div className="hebrew-day-container">
                <span className="gregorian-day">{day}</span>
                <span className="hebrew-day">{hebrewDay}</span>
            </div>
        );
    } catch (error) {
        return <span>{day}</span>;
    }
};

// Format month header to show Hebrew month
const formatMonthYear = (date) => {
    try {
        const hDate = new HDate(date);
        const gregorianMonth = date.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
        const hebrewMonth = hDate.getMonthName('h');
        const hebrewYear = numberToHebrewYear(hDate.getFullYear());
        return (
            <div className="month-year-display">
                <div className="gregorian-month">{gregorianMonth}</div>
                <div className="hebrew-month">{hebrewMonth} {hebrewYear}</div>
            </div>
        );
    } catch (error) {
        return date.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
    }
};

const HebrewDatePicker = ({
    selected,
    onChange,
    selectsStart,
    selectsEnd,
    startDate,
    endDate,
    minDate,
    maxDate,
    placeholder,
    ...rest
}) => {
    // Hebrew day names (Sunday=א to Saturday=ש)
    const hebrewDayNames = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

    return (
        <DatePicker
            selected={selected}
            onChange={onChange}
            selectsStart={selectsStart}
            selectsEnd={selectsEnd}
            startDate={startDate}
            endDate={endDate}
            minDate={minDate}
            maxDate={maxDate}
            customInput={<HebrewDateInput placeholder={placeholder} />}
            renderDayContents={renderDayContents}
            dateFormat="yyyy-MM-dd"
            calendarClassName="hebrew-calendar"
            formatWeekDay={(day) => hebrewDayNames[['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day)]}
            renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
            }) => (
                <div className="hebrew-calendar-header">
                    <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="hebrew-calendar-nav-btn"
                    >
                        ›
                    </button>
                    <div className="hebrew-calendar-month-title">
                        {formatMonthYear(date)}
                    </div>
                    <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="hebrew-calendar-nav-btn"
                    >
                        ‹
                    </button>
                </div>
            )}
            onFocus={e => e.target.blur()}
            {...rest}
        />
    );
};

export default HebrewDatePicker;
